<script setup>
import { onMounted, computed, watchEffect, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import AppSidebar from '@/components/general/Sidebar.vue';
import AppTopbar from '@/components/general/Topbar.vue';
import AppScrollTopButton from '@/components/general/ScrollTopButton.vue';
import AppRunJobToast from '@/components/actions/RunJobToast.vue';

import { useUserStore } from '@/stores/user';
import { usePermissionsStore } from '@/stores/permissions';
import { useProjectStore } from '@/stores/project';

import { useAuth } from '@/composables/useAuth';

import { handleError } from '@/services/utils.js';
import { useAuthStore } from '@/stores/auth';

const authStore = useAuthStore();
const { fetchInitialData, logoutMutationLoading } = useAuth();

const userStore = useUserStore();
const permissionsStore = usePermissionsStore();
const projectStore = useProjectStore();

const route = useRoute();
const router = useRouter();
const projectId = computed(
  () => route.params.projectId || projectStore.currentProjectId
);

const showProgressSpinner = computed(
  () =>
    (!projectId && route.path !== 'page-not-found') || route.path === '/login'
);
watch(
  () => logoutMutationLoading.value,
  (newValue) => {
    console.log('Logout loading state changed:', newValue);
  }
);
onMounted(async () => {
  if (localStorage.getItem('access_token')) {
    try {
      await fetchInitialData();

      // Check if the project exists using the route params and set it as the current project.
      const projectExists = projectStore.projects.some(
        (project) => project.project_id == projectId.value
      );
      if (projectExists) {
        projectStore.setCurrentProjectById(projectId.value);
      } else {
        router.push({ path: '/page-not-found' });
      }
    } catch (error) {
      handleError(error);
    }
  }
});
</script>

<template>
  <div v-if="authStore.logoutMutationLoading">
    <div class="grid min-h-screen items-center">
      <ProgressSpinner
        strokeWidth="8"
        class="h-[30px] w-[30px] fill-surface-50 dark:fill-surface-800"
        animationDuration=".5s"
      />
    </div>
  </div>
  <div
    v-if="
      userStore.isAuthenticated &&
      permissionsStore.permissions?.length &&
      projectStore.projects?.length
    "
    class="relative flex min-h-screen"
  >
    <div class="sticky top-0 hidden h-screen rounded-none md:block">
      <app-sidebar />
    </div>

    <div class="relative flex min-h-screen flex-auto flex-col">
      <div class="sticky top-0 z-20">
        <app-topbar />
      </div>

      <div v-if="showProgressSpinner" class="grid min-h-screen items-center">
        <ProgressSpinner
          strokeWidth="8"
          class="h-[30px] w-[30px] fill-surface-50 dark:fill-surface-800"
          animationDuration=".5s"
        />
      </div>
      <div v-else class="content-wrapper p-4 pt-8 md:p-8">
        <router-view />
        <app-scroll-top-button />
      </div>
    </div>
  </div>

  <div v-else>
    <router-view />
  </div>
  <ConfirmDialog id="confirm" class="modal" />
  <DynamicDialog />
  <Toast />
  <app-run-job-toast />
</template>

<style scoped></style>
